import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { B2C_ENABLED } from "../../config/features";
import InterestModal from "./InterestModal";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mice-latam-pro/artifacts/pdq87uby_cropped-LOGOS-IR-1.png";

const translations = {
  navLinks: {
    es: [
      { label: "Cursos", href: "#cursos" },
      { label: "Diplomado", href: "#diplomado" },
      ...(B2C_ENABLED ? [{ label: "Pack", href: "#pack" }] : []),
      { label: "Beneficios", href: "#beneficios" },
      { label: "Testimonios", href: "#testimonios" },
      { label: "FAQ", href: "#faq" },
    ],
    en: [
      { label: "Courses", href: "#cursos" },
      { label: "Diploma", href: "#diplomado" },
      ...(B2C_ENABLED ? [{ label: "Bundle", href: "#pack" }] : []),
      { label: "Benefits", href: "#beneficios" },
      { label: "Testimonials", href: "#testimonios" },
      { label: "FAQ", href: "#faq" },
    ],
  },
  cta: {
    es: B2C_ENABLED ? "Ver cursos" : "Más información",
    en: B2C_ENABLED ? "View Courses" : "More information",
  },
};

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [interestOpen, setInterestOpen] = useState(false);

  const navLinks = translations.navLinks[language];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <motion.header
        data-testid="header"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-[#0B1F3B]/95 backdrop-blur-xl shadow-lg"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#" data-testid="header-logo" className="flex items-center">
              <div className="bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm">
                <img
                  src={LOGO_URL}
                  alt="Industria de Reuniones"
                  className="h-8 md:h-10"
                />
              </div>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  className="font-body text-white/80 hover:text-[#2ED3C6] transition-colors text-sm font-medium"
                >
                  {link.label}
                </button>
              ))}
              
              {/* Language Switcher */}
              <button
                onClick={toggleLanguage}
                data-testid="language-switcher"
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-all text-sm font-medium"
              >
                <Globe className="w-4 h-4" />
                {language === 'es' ? 'EN' : 'ES'}
              </button>

              <button
                onClick={() => (B2C_ENABLED ? scrollToSection('#cursos') : setInterestOpen(true))}
                data-testid="header-cta"
                className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-6 py-2.5 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#F59E0B]/20"
              >
                {t(translations.cta)}
              </button>
            </nav>

            {/* Mobile: Language + Menu */}
            <div className="flex items-center gap-3 md:hidden">
              <button
                onClick={toggleLanguage}
                data-testid="mobile-language-switcher"
                className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/10 text-white/80 text-sm"
              >
                <Globe className="w-4 h-4" />
                {language === 'es' ? 'EN' : 'ES'}
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-toggle"
                className="text-white p-2"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#0B1F3B]/98 backdrop-blur-xl z-40 md:hidden"
          >
            <motion.nav
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.1 }}
              className="flex flex-col items-center justify-center h-full gap-8"
            >
              {navLinks.map((link, index) => (
                <motion.button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  className="text-white text-2xl font-display hover:text-[#2ED3C6] transition-colors"
                >
                  {link.label}
                </motion.button>
              ))}
              <motion.button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  if (B2C_ENABLED) {
                    scrollToSection('#cursos');
                  } else {
                    setInterestOpen(true);
                  }
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-8 py-3 text-lg transition-all mt-4"
              >
                {t(translations.cta)}
              </motion.button>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
      <InterestModal open={interestOpen} onClose={() => setInterestOpen(false)} />
    </>
  );
};

export default Header;
